import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';  
import styled from 'styled-components';

const VideoContainer = styled.div`
  width: 100%;
  height: auto;
  aspect-ratio: 16/9;
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  transform: scale(1);
  opacity: 1;
`;

const MiniVideoContainer = styled.div`
  aspect-ratio: 16/9;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  transform: scale(1);
  opacity: 1;

  @media (max-width: 768px) {
    height: 100%;
  }
`;

const VideoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(1) translateX(0) translateY(0);
  opacity: ${props => props.isMini ? '0.7' : '1'};
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 15px;
  tabindex: ${props => props.isMini ? '-1' : '0'};
`;

const TopRightOverlay = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 20;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const CountdownBox = styled.div`
  background: rgba(0,0,0,0.5);
  color: #fff;
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 12px;
  padding: 3px 10px;
  min-width: 28px;
  text-align: center;
`;

const YoutubeIframe_L = forwardRef(({ 
  videoUrl, 
  onVideoStart, 
  onVideoMini, 
  isMini = false,
  onReady 
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMiniSize, setIsMiniSize] = useState(isMini);
  const [playerReady, setPlayerReady] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [isVideoActuallyPlaying, setIsVideoActuallyPlaying] = useState(false);
  const [hasStartedCountdown, setHasStartedCountdown] = useState(false);
  const playerRef = useRef(null);
  const timerRef = useRef(null);
  const playerIdRef = useRef(`youtube-player-${Math.random().toString(36).substr(2, 9)}`);
  const youtubePlayer = useRef(null);

  // YouTube Player API 로드
  useEffect(() => {
    if (!videoUrl) return;

    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    const onYouTubeIframeAPIReady = () => {
      if (window.YT) {
        const videoId = extractVideoId(videoUrl);
        youtubePlayer.current = new window.YT.Player(playerIdRef.current, {
          height: '100%',
          width: '100%',
          videoId: videoId,
          playerVars: {
            autoplay: 0, // 자동재생 비활성화
            mute: 0, // 소리 켜기
            controls: 0, // 컨트롤 표시
            disablekb: 0, // 키보드 컨트롤 허용
            fs: '0',
            iv_load_policy: '3',
            modestbranding: '1',
            rel: '0',
            playsinline: '1',
            enablejsapi: '1', // YouTube API 활성화
            origin: window.location.origin,
            widget_referrer: window.location.origin,
            cc_load_policy: '0',
            hl: 'ko',
            loop: 1, // 루프 활성화
            playlist: videoId
          },
          events: {
            'onReady': onPlayerReady,
            'onStateChange': onPlayerStateChange
          }
        });
      }
    };

    if (window.YT && window.YT.Player) {
      onYouTubeIframeAPIReady();
    } else {
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
    }

    return () => {
      if (window.onYouTubeIframeAPIReady === onYouTubeIframeAPIReady) {
        delete window.onYouTubeIframeAPIReady;
      }
    };
  }, [videoUrl]);

  // 비디오 URL이 변경되면 상태 리셋
  useEffect(() => {
    setIsVideoActuallyPlaying(false);
    setHasStartedCountdown(false);
    setCountdown(5);
  }, [videoUrl]);

  // isMini 상태가 변경될 때 iframe 완전 비활성화
  useEffect(() => {
    if (youtubePlayer.current && playerReady) {
      try {
        const iframe = youtubePlayer.current.getIframe();
        if (iframe) {
          if (isMini) {
            // isMini일 때 iframe 완전 비활성화
            iframe.style.pointerEvents = 'none';
            iframe.setAttribute('tabindex', '-1');
            iframe.style.userSelect = 'none';
            iframe.style.webkitUserSelect = 'none';
            iframe.style.mozUserSelect = 'none';
            iframe.style.msUserSelect = 'none';
            iframe.setAttribute('aria-hidden', 'true');
          } else {
            // isMini가 아닐 때 iframe 활성화
            iframe.style.pointerEvents = 'auto';
            iframe.setAttribute('tabindex', '0');
            iframe.style.userSelect = 'auto';
            iframe.style.webkitUserSelect = 'auto';
            iframe.style.mozUserSelect = 'auto';
            iframe.style.msUserSelect = 'auto';
            iframe.removeAttribute('aria-hidden');
          }
        }
      } catch (error) {
        console.log('iframe 비활성화 실패:', error);
      }
    }
  }, [isMini, playerReady]);

  //1. 카운트다운 시작 트리거
  useEffect(() => {
    if (isVideoActuallyPlaying && !hasStartedCountdown) {
      setHasStartedCountdown(true);
      setCountdown(5);
    }
  }, [isVideoActuallyPlaying, hasStartedCountdown]);

  // 2. 카운트다운 타이머
  useEffect(() => {
    if (!hasStartedCountdown) return;
    if (countdown === 0) {
      // 카운트다운이 끝나면 onVideoMini 콜백 호출
      if (onVideoMini) onVideoMini();
      return;
    }
    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [hasStartedCountdown, countdown]);



  const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const onPlayerReady = (event) => {
    setPlayerReady(true);
    if (onReady) onReady();
  };

  const onPlayerStateChange = (event) => {
    
    // 재생 상태 변경 감지
    if (event.data === window.YT.PlayerState.PLAYING) {
      setIsPlaying(true);
      setIsVideoActuallyPlaying(true);
      if (onVideoStart) onVideoStart();
      
      // 미니사이즈 전환은 부모 컴포넌트에서 컨트롤
    } else if (event.data === window.YT.PlayerState.PAUSED || 
               event.data === window.YT.PlayerState.ENDED) {
      setIsPlaying(false);
      setIsVideoActuallyPlaying(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const Container = isMiniSize ? MiniVideoContainer : VideoContainer;

  // ref를 통해 외부에서 호출할 수 있는 함수들
  useImperativeHandle(ref, () => ({
    playVideo: () => {
      if (youtubePlayer.current && playerReady) {
        try {
          youtubePlayer.current.playVideo();
        } catch (error) {
          //console.log('YouTube player not ready yet, retrying...');
          // 플레이어가 아직 준비되지 않았으면 잠시 후 재시도
          // setTimeout(() => {
          //   if (youtubePlayer.current && youtubePlayer.current.playVideo) {
          //     youtubePlayer.current.playVideo();
          //   }
          // }, 1000);
        }
      }
    },
    pauseVideo: () => {
      if (youtubePlayer.current && playerReady) {
        try {
          youtubePlayer.current.pauseVideo();
        } catch (error) {
          //console.log('YouTube player pause failed:', error);
        }
      }
    }
  }));

  return (
    <VideoWrapper isMini={isMiniSize}>
      <Container>
        {/* 오른쪽 상단 카운트다운 */}
        {countdown > 0 && (
          <TopRightOverlay>
            <CountdownBox>{countdown}</CountdownBox>
          </TopRightOverlay>
        )}
        <div id={playerIdRef.current}></div>
      </Container>
    </VideoWrapper>
  );
});

export default YoutubeIframe_L; 